import { useEffect, useMemo, useState } from 'react'
import { Box, Container, Tab, Tabs, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import { useTheme } from '@mui/material/styles'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import TablePagination from 'src/v3/components/table-pagination'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { EventInviteStatus } from 'src/v3/core/types'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import {
  activeTabChanged,
  fetchAccreditations,
  fetchEventAccreditations,
  fetchEvents,
  fetchInstructors,
  fetchLinkedServices,
  fetchSessions,
  selectAccreditationsStatus,
  selectActiveTab,
  selectEventAccreditationsStatus,
  selectEventAttentionByEventId,
  selectEvents,
  selectEventsStatus,
  selectFilteredSessions,
  selectInstructorsStatus,
  selectLinkedServicesStatus,
  selectSessions,
  selectSessionsStatus,
} from './events-v2.slice'
import type { EventsTab } from './types'
import type { EventAttention } from './utils/session-attention'
import { isSessionPast } from './utils/access-window'
import { getRsvpColors } from './utils/constants'
import SessionRow from './components/session-row'
import SessionRowSkeleton from './components/session-row-skeleton'
import tabLabel from './components/tab-label'
import EventsFilterPanel from './components/events-filter-panel'
import EventsCalendar from './components/events-calendar'

let emptyMessages: Record<Exclude<EventsTab, 'calendar'>, string> = {
  upcoming: 'No upcoming sessions match your filters',
  attention: 'No sessions requiring attention match your filters',
  invited: 'You have no pending invitations for upcoming sessions',
  registered: "You haven't registered for any sessions",
  attended: "You don't have any recorded session attendance",
}

// The one session per event that needs something from the user (attendance or requirements).
let needsAttention = (
  s: { id: string; eventID: string },
  attentionByEvent: Map<string, EventAttention>,
) => attentionByEvent.get(s.eventID)?.sessionId === s.id

dayjs.extend(utc)

let EventsV2Page = () => {
  useScrollToTop()
  let dispatch = useAppDispatch()
  let theme = useTheme()
  let { showSuccess, showError, NotificationComponent } = useNotifications()
  let activeTab = useAppSelector(selectActiveTab)
  let sessions = useAppSelector(selectFilteredSessions)
  let allSessions = useAppSelector(selectSessions)
  let events = useAppSelector(selectEvents)

  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let instructorsStatus = useAppSelector(selectInstructorsStatus)
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(eventsStatus, fetchEvents)
  useAutoFetch(sessionsStatus, fetchSessions)
  useAutoFetch(instructorsStatus, fetchInstructors)
  useAutoFetch(accreditationsStatus, fetchAccreditations)
  useAutoFetch(eventAccreditationsStatus, fetchEventAccreditations)

  let attentionByEvent = useAppSelector(selectEventAttentionByEventId)

  let tabSessions = useMemo(() => {
    let byStatus = sessions.reduce(
      (acc, s) => {
        let status = s.invites?.[0]?.status
        if (!status) return acc
        // eslint-disable-next-line fp/no-mutation
        if (!acc[status]) acc[status] = []
        // eslint-disable-next-line fp/no-mutating-methods
        acc[status]!.push(s)
        return acc
      },
      {} as Partial<Record<EventInviteStatus, typeof sessions>>,
    )
    let sortDesc = (arr: typeof sessions) =>
      // eslint-disable-next-line fp/no-mutating-methods
      [...arr].sort((a, b) =>
        dayjs.utc(b.sessionTime).diff(dayjs.utc(a.sessionTime)),
      )
    // eslint-disable-next-line fp/no-mutating-methods
    let upcomingSessions = sessions
      .filter((s) => !isSessionPast(s.sessionTime, s.endTime))
      .sort((a, b) => dayjs.utc(a.sessionTime).diff(dayjs.utc(b.sessionTime)))
    return {
      upcoming: upcomingSessions,
      attention: sortDesc(
        sessions.filter((s) => needsAttention(s, attentionByEvent)),
      ),
      invited: sortDesc(byStatus.invited ?? []),
      registered: sortDesc(byStatus.accepted ?? []),
      attended: sortDesc(byStatus.attended ?? []),
      calendar: [] as typeof sessions, // placeholder — keeps tabSessions[activeTab] type-safe; calendar renders allSessions directly
    }
  }, [sessions, attentionByEvent])

  let upcomingCount = tabSessions.upcoming.length
  let attentionCount = tabSessions.attention.length
  let invitedCount = tabSessions.invited.length
  let registeredCount = tabSessions.registered.length
  let attendedCount = tabSessions.attended.length

  let isDataLoading =
    sessionsStatus === 'pending' ||
    sessionsStatus === 'uninitialized' ||
    eventsStatus === 'pending' ||
    eventsStatus === 'uninitialized'
  let isDataError = sessionsStatus === 'rejected' || eventsStatus === 'rejected'
  let isDataReady =
    sessionsStatus === 'fulfilled' && eventsStatus === 'fulfilled'

  let countOrDash = (n: number): number | string => (isDataReady ? n : '-')

  // Visibility uses the unfiltered sessions so a search can't hide the tab while something
  // is still outstanding; the list itself honors the filters like every other tab.
  let showAttention =
    isDataReady && allSessions.some((s) => needsAttention(s, attentionByEvent))
  // A vanished tab falls back to Upcoming without touching the stored selection.
  let effectiveTab: EventsTab =
    activeTab === 'attention' && !showAttention ? 'upcoming' : activeTab

  let [page, setPage] = useState(1)
  let [pageSize, setPageSize] = useState(25)

  useEffect(() => {
    setPage(1)
  }, [effectiveTab, sessions])

  let activeTabSessions = tabSessions[effectiveTab]
  let pagedSessions = activeTabSessions.slice(
    (page - 1) * pageSize,
    page * pageSize,
  )

  let handleTabChange = (_: unknown, value: EventsTab) => {
    dispatch(activeTabChanged(value))
  }

  return (
    <Container maxWidth="xl" component="main" className="p-6">
      <Typography variant="h5" component="h1" className="mb-4">
        Events
      </Typography>
      <Tabs
        value={effectiveTab}
        onChange={handleTabChange}
        aria-label="Events navigation tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab
          id="events-tab-upcoming"
          aria-controls="events-tabpanel-upcoming"
          value="upcoming"
          label={tabLabel(
            'Upcoming',
            countOrDash(upcomingCount),
            'text.secondary',
          )}
        />
        {showAttention && (
          <Tab
            id="events-tab-attention"
            aria-controls="events-tabpanel-attention"
            value="attention"
            label={tabLabel(
              'Requires attention',
              countOrDash(attentionCount),
              'warning.main',
            )}
          />
        )}
        <Tab
          id="events-tab-invited"
          aria-controls="events-tabpanel-invited"
          value="invited"
          label={tabLabel(
            'Invited',
            countOrDash(invitedCount),
            getRsvpColors(theme).invited,
          )}
        />
        <Tab
          id="events-tab-registered"
          aria-controls="events-tabpanel-registered"
          value="registered"
          label={tabLabel(
            'Registered',
            countOrDash(registeredCount),
            getRsvpColors(theme).accepted,
          )}
        />
        <Tab
          id="events-tab-attended"
          aria-controls="events-tabpanel-attended"
          value="attended"
          label={tabLabel(
            'Attended',
            countOrDash(attendedCount),
            getRsvpColors(theme).attended,
          )}
        />
        <Tab
          id="events-tab-calendar"
          aria-controls="events-tabpanel-calendar"
          value="calendar"
          label="Calendar"
        />
      </Tabs>

      {effectiveTab !== 'calendar' && (
        <EventsFilterPanel filteredCount={activeTabSessions.length} />
      )}

      {(
        [
          'upcoming',
          'attention',
          'invited',
          'registered',
          'attended',
        ] satisfies EventsTab[]
      ).map((tab) => (
        <Box
          key={tab}
          role="tabpanel"
          id={`events-tabpanel-${tab}`}
          aria-labelledby={`events-tab-${tab}`}
          hidden={effectiveTab !== tab}
          className="pt-4"
        >
          {effectiveTab === tab && (
            <>
              {isDataError ? (
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        dispatch(fetchSessions())
                        dispatch(fetchEvents())
                      }}
                    >
                      Retry
                    </Button>
                  }
                >
                  Could not load sessions or events.
                </Alert>
              ) : isDataLoading ? (
                <>
                  <span className="sr-only" role="status" aria-live="polite">
                    Loading sessions…
                  </span>
                  <Box
                    component="ul"
                    role="list"
                    sx={{ listStyle: 'none', p: 0, m: 0 }}
                  >
                    {Array.from({ length: pageSize }).map((_, i) => (
                      <li key={i} aria-hidden="true">
                        <SessionRowSkeleton />
                      </li>
                    ))}
                  </Box>
                </>
              ) : activeTabSessions.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="py-8 text-center"
                >
                  {emptyMessages[tab]}
                </Typography>
              ) : (
                <>
                  <Box
                    component="ul"
                    role="list"
                    sx={{ listStyle: 'none', p: 0, m: 0 }}
                  >
                    {pagedSessions.map((s) => (
                      <li key={s.id}>
                        <SessionRow
                          sessionId={s.id}
                          onSuccess={showSuccess}
                          onError={showError}
                        />
                      </li>
                    ))}
                  </Box>
                  <TablePagination
                    total={activeTabSessions.length}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size)
                      setPage(1)
                    }}
                    itemLabel="sessions"
                  />
                </>
              )}
            </>
          )}
        </Box>
      ))}

      <Box
        role="tabpanel"
        id="events-tabpanel-calendar"
        aria-labelledby="events-tab-calendar"
        hidden={effectiveTab !== 'calendar'}
        className="pt-4"
      >
        {effectiveTab === 'calendar' && (
          <EventsCalendar sessions={allSessions} events={events} />
        )}
      </Box>

      {NotificationComponent}
    </Container>
  )
}

export default EventsV2Page
